import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Grid,
  TextField,
  FormLabel,
  Button,
  Autocomplete,
  FormControl,
  Checkbox,
  Fade,
  Paper,
  Typography,
  Divider,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Tooltip,
} from "@mui/material";
import {
  AttachFile,
  Delete,
  Add,
  Edit,
  MenuBook,
  DeleteForever,
  Star,
  StarBorder,
} from "@mui/icons-material";
import Swal from "sweetalert2";
import {
  CommonSubHeader,
  useLocationHelpers,
  NavButton,
  normalizeMultilineText,
} from "../../Components/helpers/Helpers";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../../api/axios";
import LoadPdf from "../../Biblioteca/Crud/LoadPdf/LoadPdf";

const SI_NO_OPTIONS = [
  { nombre: "Seleccionar", id: -1 },
  { nombre: "Si", id: 1 },
  { nombre: "No", id: 0 },
];

const Editar = () => {
  const navigate = useNavigate();
  const { goView, goTable, buildViewUrl, buildTableUrl } = useLocationHelpers();
  const [searchParams] = useSearchParams();
  const associationId = searchParams.get("id");
  const fileInputRef = useRef(null);

  /* --- existing docs from server --- */
  const [existingDocs, setExistingDocs] = useState([]);
  const [principalDoc, setPrincipalDoc] = useState(null); // stored principal
  const [selectedDoc, setSelectedDoc] = useState(null); // for PDF viewer

  /* --- new files chosen by user --- */
  const [newFiles, setNewFiles] = useState([]);
  const [newFilesDynamicNames, setNewFilesDynamicNames] = useState([]);
  const [newPrincipalIndex, setNewPrincipalIndex] = useState(null); // index in newFiles if user wants a new file as principal

  const [options, setOptions] = useState({
    companies: [],
    provinces: [],
    groups: [],
    notifications: SI_NO_OPTIONS,
    agreementSigned: SI_NO_OPTIONS,
  });

  const [loading, setLoading] = useState(false);

  const normalizeDocs = (data) => {
    if (Array.isArray(data?.files_meta) && data.files_meta.length > 0) {
      return data.files_meta.map((doc) => ({
        original: doc.original,
        nombre_dinamico: doc.nombre_dinamico ?? "",
        display_name: doc.display_name ?? doc.original,
      }));
    }

    const files = data?.files ?? (data?.file ? [data.file] : []);
    return files.map((file) => ({
      original: file,
      nombre_dinamico: "",
      display_name: file,
    }));
  };

  const [associationData, setAssociationData] = useState({
    associationCompany: null,
    associationName: "",
    associationGroups: null,
    associationNotifications: null,
    associationContactPerson: "",
    associationCif: "",
    associationAddress: "",
    associationProvince: null,
    associationPostalCode: "",
    associationEmail: "",
    associationAgreementSigned: null,
    associationPhone: "",
  });

  const [associationObservations, setAssociationObservations] = useState("");
  const [marketingToSend, setMarketingToSend] = useState(0);
  const [marketingToShow, setMarketingToShow] = useState(false);

  const alertError = (title, text) =>
    Swal.fire({ title, icon: "error", text, confirmButtonColor: "#1ab394" });

  const findOption = (list, id) =>
    list.find((o) => o.id === id) ?? list[0] ?? null;

  /* --- fetch --- */
  const checkDocument = async () => {
    try {
      const { data } = await api.post(
        `${process.env.REACT_APP_API_URL}/associations/check-document`,
        { id: associationId },
      );
      const files = normalizeDocs(data);
      const principal =
        data?.principal_doc_meta?.original ??
        data?.principal_doc ??
        files?.[0]?.original ??
        null;

      setExistingDocs(files);
      setPrincipalDoc(principal);
      setSelectedDoc(principal ?? files?.[0]?.original ?? null);
    } catch {
      setExistingDocs([]);
      setPrincipalDoc(null);
      setSelectedDoc(null);
    }
  };

  const getData = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(
        `${process.env.REACT_APP_API_URL}/associations/edit`,
        { id: associationId },
      );

      const assoc = data.assoc?.[0] ?? {};

      setMarketingToShow(assoc.marketing !== 0);
      setMarketingToSend(assoc.marketing !== 0 ? 1 : 0);

      setAssociationObservations(
        normalizeMultilineText(assoc.asoc_observaciones),
      );

      setAssociationData({
        associationCompany: assoc.company_relation ?? null,
        associationName: assoc.asoc_name ?? "",
        associationGroups: assoc.group_relations ?? null,
        associationNotifications: findOption(
          SI_NO_OPTIONS,
          assoc.asoc_notifications,
        ),
        associationContactPerson: assoc.asoc_contactname ?? "",
        associationCif: assoc.asoc_cif ?? "",
        associationAddress: assoc.asoc_address ?? "",
        associationProvince: assoc.provinces_relation ?? null,
        associationPostalCode: assoc.asoc_postalcode ?? "",
        associationEmail: assoc.asoc_email ?? "",
        associationAgreementSigned: findOption(
          SI_NO_OPTIONS,
          assoc.asoc_acuerdo,
        ),
        associationPhone: assoc.asoc_phone ?? "",
      });

      setOptions({
        companies: [
          { company_name: "Seleccionar", id: 0 },
          ...(data.companies ?? []),
        ],
        provinces: [
          { provincia: "Seleccionar", id: 0 },
          ...(data.provinces ?? []),
        ],
        groups: [{ grupo: "Seleccionar", id: 0 }, ...(data.groups ?? [])],
        notifications: SI_NO_OPTIONS,
        agreementSigned: SI_NO_OPTIONS,
      });

      await checkDocument();
    } catch {
      alertError("Error", "No se pudieron obtener los datos de la asociacion.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* --- form fields --- */
  const formFields = [
    {
      label: "Empresa",
      name: "associationCompany",
      type: "autocomplete",
      options: options.companies,
      isOptionEqualToValue: (o, v) => o?.company_name === v?.company_name,
      getOptionLabel: (o) => o?.company_name || "",
    },
    {
      label: "Provincia",
      name: "associationProvince",
      type: "autocomplete",
      options: options.provinces,
      isOptionEqualToValue: (o, v) => o?.id === v?.id,
      getOptionLabel: (o) => o?.provincia || "",
    },
    {
      label: "Grupos",
      name: "associationGroups",
      type: "autocomplete",
      options: options.groups,
      isOptionEqualToValue: (o, v) => o?.id === v?.id,
      getOptionLabel: (o) => o?.grupo || "",
    },
    {
      label: "Notificaciones",
      name: "associationNotifications",
      type: "autocomplete",
      options: SI_NO_OPTIONS,
      isOptionEqualToValue: (o, v) => o?.id === v?.id,
      getOptionLabel: (o) => o?.nombre || "",
    },
    {
      label: "Acuerdo firmado",
      name: "associationAgreementSigned",
      type: "autocomplete",
      options: SI_NO_OPTIONS,
      isOptionEqualToValue: (o, v) => o?.id === v?.id,
      getOptionLabel: (o) => o?.nombre || "",
    },
    { name: "associationName", label: "Nombre", type: "text" },
    { name: "associationPhone", label: "Telefono", type: "text" },
    {
      name: "associationContactPerson",
      label: "Persona de contacto",
      type: "text",
    },
    { name: "associationCif", label: "CIF", type: "text" },
    { name: "associationAddress", label: "Direccion", type: "text" },
    {
      name: "associationPostalCode",
      label: "Codigo postal",
      type: "text",
      length: 10,
      inputMode: "numeric",
    },
    { name: "associationEmail", label: "Email", type: "text" },
    {
      name: "associationObservations",
      label: "Observaciones",
      type: "textarea",
    },
  ];

  /* --- validation --- */
  const isFilled = (val) => {
    if (val == null) return false;
    if (typeof val === "string") return val.trim() !== "";
    if (typeof val === "object" && "id" in val)
      return val.id !== 0 && val.id !== -1;
    return true;
  };

  const requiredFieldNames = [
    "associationCompany",
    "associationName",
    "associationGroups",
    "associationProvince",
    "associationPostalCode",
    "associationContactPerson",
    "associationEmail",
    "associationPhone",
  ];

  const fieldLabelMap = Object.fromEntries(
    formFields.map((f) => [f.name, f.label]),
  );

  const validateFields = () =>
    requiredFieldNames.every((n) => isFilled(associationData[n]));

  const showValidationErrors = () => {
    const missing = requiredFieldNames
      .filter((n) => !isFilled(associationData[n]))
      .map((n) => `<li>${fieldLabelMap[n] ?? n}</li>`)
      .join("");
    Swal.fire({
      title: "Campos incompletos",
      icon: "error",
      html: `<ul>${missing}</ul>`,
      confirmButtonColor: "#1ab394",
    });
  };

  /* --- handlers --- */
  const handleChange = (e) => {
    let { name, value } = e.target;
    if (name === "associationPostalCode")
      value = value.replace(/[^0-9]/g, "").slice(0, 10);
    setAssociationData({ ...associationData, [name]: value });
  };

  const handleAutocompleteChange = (_, value, field) =>
    setAssociationData((prev) => ({ ...prev, [field]: value }));

  const getAutocompleteOptionLabel = (option) =>
    option?.grupo ??
    option?.company_name ??
    option?.provincia ??
    option?.nombre ??
    "";

  const renderAutocompleteOption = (props, option) => {
    const { key, ...rest } = props;
    const label = getAutocompleteOptionLabel(option);
    const uniqueKey = `${option?.id ?? "no-id"}-${label}`;

    return (
      <li {...rest} key={uniqueKey}>
        {label}
      </li>
    );
  };

  const handleChangeCheckbox = () => {
    setMarketingToSend((prev) => (prev ? 0 : 1));
    setMarketingToShow((prev) => !prev);
  };

  const handleAddFiles = (e) => {
    const existingNames = [
      ...existingDocs.map((d) => d.original),
      ...newFiles.map((f) => f.name),
    ];
    const incoming = Array.from(e.target.files ?? []).filter((f) => {
      if (f.type !== "application/pdf") return false;
      if (existingNames.includes(f.name)) return false;
      return true;
    });
    if (incoming.length < Array.from(e.target.files ?? []).length) {
      Swal.fire({
        icon: "warning",
        title: "Archivos duplicados",
        text: "Algunos archivos ya existen o no son PDF y fueron omitidos.",
        confirmButtonColor: "#1ab394",
        timer: 2500,
        showConfirmButton: false,
      });
    }
    setNewFiles((prev) => [...prev, ...incoming]);
    setNewFilesDynamicNames((prev) => [...prev, ...incoming.map(() => "")]);
    e.target.value = "";
  };

  const handleRemoveNewFile = (index) => {
    setNewFiles((prev) => prev.filter((_, i) => i !== index));
    setNewFilesDynamicNames((prev) => prev.filter((_, i) => i !== index));
    if (newPrincipalIndex === index) setNewPrincipalIndex(null);
    else if (newPrincipalIndex !== null && newPrincipalIndex > index)
      setNewPrincipalIndex((p) => p - 1);
  };

  const handleChangeNewDynamicName = (index, value) => {
    setNewFilesDynamicNames((prev) =>
      prev.map((name, i) => (i === index ? value : name)),
    );
  };

  /* --- delete existing doc --- */
  const handleDeleteDoc = async (doc) => {
    let fileName = doc?.original;
    let dinamicName = doc?.nombre_dinamico;

    const confirm = await Swal.fire({
      title: "Eliminar documento",
      text: `Seguro que deseas eliminar "${dinamicName ? dinamicName : fileName}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
      reverseButtons: true,
    });
    if (!confirm.isConfirmed) return;

    Swal.fire({
      title: "Eliminando...",
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      await api.post(
        `${process.env.REACT_APP_API_URL}/associations/delete-document`,
        { id: associationId, file_name: fileName },
      );

      // If this was the principal, save null principal to DB
      if (fileName === principalDoc) {
        await api.post(`${process.env.REACT_APP_API_URL}/associations/update`, {
          id: associationId,
          asoc_principal_doc: "",
        });
      }

      Swal.fire({
        icon: "success",
        title: "Documento eliminado",
        timer: 1600,
        showConfirmButton: false,
      });

      setExistingDocs((prev) => {
        const updated = prev.filter((f) => f.original !== fileName);
        if (fileName === principalDoc) {
          const next = updated[0]?.original ?? null;
          setPrincipalDoc(next);
          setSelectedDoc(next);
        } else if (selectedDoc === fileName) {
          setSelectedDoc(updated[0]?.original ?? null);
        }
        return updated;
      });
    } catch {
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo eliminar" });
    }
  };

  /* --- set principal from existing docs --- */
  const handleSetPrincipal = async (fileName) => {
    // Optimistic UI
    const prev = principalDoc;
    setPrincipalDoc(fileName);
    try {
      const doc = existingDocs.find((d) => d.original === fileName);
      await api.post(`${process.env.REACT_APP_API_URL}/associations/update`, {
        id: associationId,
        asoc_principal_doc: {
          original: fileName,
          nombre_dinamico: doc?.nombre_dinamico || null,
        },
      });
    } catch {
      setPrincipalDoc(prev);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudo actualizar el documento principal",
      });
    }
  };

  const handleRenameDoc = async (doc) => {
    const result = await Swal.fire({
      title: "Renombrar documento",
      text: "Este nombre solo se muestra en pantalla. El archivo real no se mueve.",
      input: "text",
      inputLabel: "Nombre dinamico",
      inputValue: doc.nombre_dinamico ?? "",
      inputPlaceholder: "Ej: Acuerdo firmado 2026",
      showCancelButton: true,
      confirmButtonText: "Guardar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#1ab394",
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    const dynamicName = (result.value ?? "").trim();

    try {
      await api.post(`${process.env.REACT_APP_API_URL}/associations/update`, {
        id: associationId,
        asoc_doc_dynamic_name: {
          original: doc.original,
          nombre_dinamico: dynamicName || null,
        },
      });

      await checkDocument();
    } catch {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudo actualizar el nombre dinamico",
      });
    }
  };

  /* --- submit --- */
  const handleSubmit = async () => {
    if (!validateFields()) return showValidationErrors();

    const formData = new FormData();

    const payload = {
      id: associationId,
      company_id: associationData.associationCompany?.id ?? "",
      asoc_name: associationData.associationName ?? "",
      asoc_grupo: associationData.associationGroups?.id ?? "",
      asoc_notifications: associationData.associationNotifications?.id ?? 0,
      asoc_contactname: associationData.associationContactPerson ?? "",
      asoc_cif: associationData.associationCif ?? "",
      asoc_address: associationData.associationAddress ?? "",
      asoc_province: associationData.associationProvince?.provincia ?? "",
      asoc_postalcode: associationData.associationPostalCode ?? "",
      asoc_email: associationData.associationEmail ?? "",
      asoc_acuerdo: associationData.associationAgreementSigned?.id ?? 0,
      asoc_phone: associationData.associationPhone ?? "",
      asoc_observaciones: normalizeMultilineText(associationObservations),
      marketing: marketingToSend ? 1 : 0,
    };

    Object.entries(payload).forEach(([k, v]) => formData.append(k, v));

    // If user picked a new file as principal, send its name hint via index
    // The backend will compute the actual stored name after uploading
    if (newPrincipalIndex !== null && newFiles[newPrincipalIndex]) {
      // We flag this so the backend sets the principal after upload
      formData.append("new_principal_index", newPrincipalIndex);
    } else {
      // Keep current principal doc
      const currentDoc = existingDocs.find((d) => d.original === principalDoc);
      formData.append(
        "asoc_principal_doc",
        JSON.stringify({
          original: principalDoc ?? null,
          nombre_dinamico: (currentDoc?.nombre_dinamico ?? "").trim() || null,
        }),
      );
    }

    const newFilesMeta = newFiles.map((file, index) => ({
      index,
      original: file.name,
      nombre_dinamico: (newFilesDynamicNames[index] ?? "").trim() || null,
    }));

    formData.append("new_files_dynamic_meta", JSON.stringify(newFilesMeta));

    newFiles.forEach((file) => {
      formData.append("agreement_files[]", file);
    });

    const confirm = await Swal.fire({
      title: "Desea guardar los cambios?",
      icon: "warning",
      showDenyButton: true,
      confirmButtonColor: "#1ab394",
      denyButtonColor: "#d33",
      reverseButtons: true,
      confirmButtonText: "Guardar",
      denyButtonText: "Volver",
    });
    if (!confirm.isConfirmed) return;

    try {
      Swal.fire({
        title: "Guardando...",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      const res = await api.post(
        `${process.env.REACT_APP_API_URL}/associations/update`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );

      console.log("Response from update API:", res.data);

      Swal.close();
      if (res.data?.error)
        return alertError("Error", "No se guardaron los cambios.");

      if (newFiles.length > 0) {
        await checkDocument();
        setNewFiles([]);
        setNewFilesDynamicNames([]);
        setNewPrincipalIndex(null);
      }

      await Swal.fire({
        title: "Guardado",
        icon: "success",
        confirmButtonText: "Ver Asociacion",
        confirmButtonColor: "#1ab394",
      });

      navigate("/asociaciones/ver?id=" + associationId);
    } catch (error) {
      Swal.close();

      console.error("DATA DEL BACKEND:", error.response?.data);

      console.error("======================================");

      alertError("Error", "Hubo un problema al guardar.");
    }
  };

  /* --- render --- */
  return (
    <div className="fatherContainer">
      <CommonSubHeader title="Asociaciones" subTitle="Editar" />

      <Fade in={!loading}>
        <Box
          sx={{
            maxWidth: 1100,
            mx: "auto",
            mt: { xs: 1, md: 2 },
            px: { xs: 1, md: 1 },
          }}
        >
          {/* Main form */}
          <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, fontSize: { xs: 18, md: 20 } }}
            >
              Editar informacion de la asociacion
            </Typography>
            <Divider sx={{ my: 2 }} />

            <Grid container spacing={{ xs: 1.5, md: 2 }}>
              {formFields.map((field, idx) => {
                if (field.type === "autocomplete") {
                  return (
                    <Grid item xs={12} sm={6} key={idx}>
                      <Autocomplete
                        value={associationData[field.name] ?? null}
                        onChange={(_, val) =>
                          handleAutocompleteChange(_, val, field.name)
                        }
                        size="small"
                        options={field.options}
                        isOptionEqualToValue={field.isOptionEqualToValue}
                        getOptionLabel={field.getOptionLabel}
                        renderOption={renderAutocompleteOption}
                        renderInput={(params) => (
                          <TextField {...params} label={field.label} />
                        )}
                      />
                    </Grid>
                  );
                }
                if (field.type === "textarea") {
                  return (
                    <Grid item xs={12} key={idx}>
                      <FormControl fullWidth>
                        <FormLabel sx={{ textAlign: "left", fontSize: 13 }}>
                          Observaciones
                        </FormLabel>
                        <textarea
                          value={associationObservations}
                          style={{
                            height: "110px",
                            resize: "vertical",
                            padding: "8px 13px",
                            fontSize: "14px",
                            borderRadius: "10px",
                            border: "1px solid rgba(128,128,128,0.35)",
                            fontFamily: "inherit",
                          }}
                          onChange={(e) =>
                            setAssociationObservations(e.target.value)
                          }
                        />
                      </FormControl>
                    </Grid>
                  );
                }
                return (
                  <Grid item xs={12} sm={6} key={idx}>
                    <TextField
                      name={field.name}
                      label={field.label}
                      value={associationData[field.name] ?? ""}
                      inputProps={
                        field.length
                          ? {
                              maxLength: field.length,
                              inputMode: field.inputMode,
                            }
                          : undefined
                      }
                      onChange={handleChange}
                      size="small"
                      fullWidth
                    />
                  </Grid>
                );
              })}

              {/* Marketing */}
              <Grid item xs={12}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: { xs: 1.5, md: 2 },
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                  }}
                >
                  <Checkbox
                    checked={marketingToShow && marketingToSend === 1}
                    onClick={handleChangeCheckbox}
                    color="success"
                    sx={{ mt: "2px" }}
                  />
                  <Typography fontSize={14}>
                    Acepto el uso de mi correo electronico para el envio de
                    comunicaciones informativas y comerciales.
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* Botones */}
            <Box sx={{ mt: 3 }}>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={4}>
                  <Button
                    fullWidth
                    onClick={handleSubmit}
                    variant="contained"
                    color="success"
                  >
                    Guardar
                  </Button>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <NavButton
                    fullWidth
                    to={buildViewUrl("asociaciones", associationId)}
                    onClick={() =>
                      goView("asociaciones", associationId, "asociacion")
                    }
                    variant="outlined"
                    color="info"
                  >
                    Ver
                  </NavButton>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <NavButton
                    fullWidth
                    to={buildTableUrl("asociaciones")}
                    onClick={() => goTable("asociaciones", "Asociaciones")}
                    variant="outlined"
                    color="error"
                  >
                    Tabla
                  </NavButton>
                </Grid>
              </Grid>
            </Box>
          </Paper>

          {/* Documents panel */}
          <Paper
            elevation={2}
            sx={{ p: { xs: 2, md: 3 }, borderRadius: 2, my: 3 }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 1,
                mb: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <MenuBook color="info" />
                <Typography sx={{ fontWeight: 600, fontSize: 15 }}>
                  Documentos adjuntos
                </Typography>
                <Chip
                  label={existingDocs.length}
                  size="small"
                  color="info"
                  variant="outlined"
                />
              </Box>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Add />}
                onClick={() => fileInputRef.current?.click()}
              >
                Anadir documento
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf"
                multiple
                style={{ display: "none" }}
                onChange={handleAddFiles}
              />
            </Box>

            {existingDocs.length > 0 && (
              <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 1 }}>
                Pulsa la estrella para marcar como principal. Pulsa el nombre
                para visualizar.
              </Typography>
            )}

            {/* Existing docs */}
            {existingDocs.length > 0 ? (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                {existingDocs.map((doc) => {
                  const isPrincipal = doc.original === principalDoc;
                  const isViewing = doc.original === selectedDoc;
                  return (
                    <Box
                      key={doc.original}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        border: isPrincipal
                          ? "1.5px solid #f5a623"
                          : isViewing
                            ? "1.5px solid #1976d2"
                            : "1px solid rgba(0,0,0,0.15)",
                        borderRadius: "20px",
                        pl: 0.5,
                        pr: 1,
                        bgcolor: isPrincipal
                          ? "#fffbf0"
                          : isViewing
                            ? "#e3f2fd"
                            : "transparent",
                        gap: 0.5,
                      }}
                    >
                      {/* Star toggle for principal */}
                      <Tooltip
                        title={
                          isPrincipal
                            ? "Documento principal"
                            : "Establecer como principal"
                        }
                      >
                        <IconButton
                          size="small"
                          onClick={() => handleSetPrincipal(doc.original)}
                          sx={{ p: 0.3 }}
                        >
                          {isPrincipal ? (
                            <Star sx={{ color: "#f5a623", fontSize: 17 }} />
                          ) : (
                            <StarBorder sx={{ color: "#bbb", fontSize: 17 }} />
                          )}
                        </IconButton>
                      </Tooltip>

                      {/* Click chip label to view PDF */}
                      <Box
                        onClick={() => setSelectedDoc(doc.original)}
                        sx={{
                          fontSize: 13,
                          cursor: "pointer",
                          maxWidth: 200,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          color: isViewing ? "#1976d2" : "text.primary",
                          fontWeight: isViewing ? 600 : 400,
                        }}
                      >
                        {doc.display_name}
                      </Box>

                      <Tooltip title="Editar nombre dinamico">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleRenameDoc(doc)}
                          sx={{ p: 0.3 }}
                        >
                          <Edit sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>

                      {isPrincipal && (
                        <Typography
                          sx={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: "#f5a623",
                            bgcolor: "#fff3cc",
                            px: 0.7,
                            borderRadius: 1,
                            lineHeight: 1.6,
                          }}
                        >
                          PRINCIPAL
                        </Typography>
                      )}

                      {/* Delete button */}
                      <Tooltip title="Eliminar documento">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteDoc(doc)}
                          sx={{ p: 0.3 }}
                        >
                          <DeleteForever sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 2 }}>
                Sin documentos guardados.
              </Typography>
            )}

            {/* New files pending */}
            {newFiles.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    mb: 1,
                    color: "success.main",
                  }}
                >
                  Nuevos documentos a subir ({newFiles.length}):
                </Typography>
                <Typography
                  sx={{ fontSize: 11, color: "text.secondary", mb: 0.5 }}
                >
                  Pulsa la estrella para marcar uno como nuevo principal.
                </Typography>
                <List dense disablePadding>
                  {newFiles.map((file, i) => {
                    const isNewPrincipal = i === newPrincipalIndex;
                    return (
                      <ListItem
                        key={i}
                        disablePadding
                        sx={{
                          py: 0.5,
                          px: 1,
                          borderRadius: 1,
                          border: isNewPrincipal
                            ? "1px solid #f5a623"
                            : "1px solid transparent",
                          bgcolor: isNewPrincipal ? "#fffbf0" : "transparent",
                          mb: 0.3,
                          "&:hover": {
                            bgcolor: isNewPrincipal
                              ? "#fffbf0"
                              : "action.hover",
                          },
                        }}
                      >
                        <Tooltip
                          title={
                            isNewPrincipal
                              ? "Sera el nuevo principal"
                              : "Establecer como nuevo principal"
                          }
                        >
                          <IconButton
                            size="small"
                            onClick={() =>
                              setNewPrincipalIndex(isNewPrincipal ? null : i)
                            }
                            sx={{ mr: 0.5 }}
                          >
                            {isNewPrincipal ? (
                              <Star sx={{ color: "#f5a623", fontSize: 17 }} />
                            ) : (
                              <StarBorder
                                sx={{ color: "#bbb", fontSize: 17 }}
                              />
                            )}
                          </IconButton>
                        </Tooltip>
                        <AttachFile
                          sx={{ fontSize: 16, color: "success.main", mr: 1 }}
                        />
                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                flexWrap: "wrap",
                              }}
                            >
                              <span style={{ fontSize: 13 }}>{file.name}</span>
                              {isNewPrincipal && (
                                <Typography
                                  sx={{
                                    fontSize: 10,
                                    fontWeight: 700,
                                    color: "#f5a623",
                                    bgcolor: "#fff3cc",
                                    px: 0.7,
                                    borderRadius: 1,
                                  }}
                                >
                                  NUEVO PRINCIPAL
                                </Typography>
                              )}
                              <TextField
                                size="small"
                                placeholder="Nombre dinamico (opcional)"
                                value={newFilesDynamicNames[i] ?? ""}
                                onChange={(e) =>
                                  handleChangeNewDynamicName(i, e.target.value)
                                }
                                sx={{ minWidth: 220 }}
                              />
                            </Box>
                          }
                        />
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveNewFile(i)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </ListItem>
                    );
                  })}
                </List>
                <Typography
                  sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}
                >
                  Los documentos se subiran al guardar.
                </Typography>
              </Box>
            )}

            {/* PDF viewer — key forces remount on doc change */}
            {selectedDoc && (
              <>
                <Divider sx={{ mb: 2 }} />
                <Chip
                  icon={<MenuBook color="info" />}
                  label={
                    existingDocs.find((d) => d.original === selectedDoc)
                      ?.display_name ?? selectedDoc
                  }
                  sx={{
                    mb: 1.5,
                    fontWeight: 600,
                    fontSize: "13px",
                    px: 1,
                    borderRadius: "8px",
                    background: "linear-gradient(135deg,#eef3ff,#f8faff)",
                    color: "#3f51b5",
                    border: "1px solid rgba(63,81,181,0.25)",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                  }}
                />
                <LoadPdf
                  key={selectedDoc}
                  bookName={selectedDoc}
                  bookId={associationId}
                  alternativeRute={"acuerdo/" + selectedDoc}
                />
              </>
            )}
          </Paper>
        </Box>
      </Fade>
    </div>
  );
};

export default Editar;
